import React, { useMemo, useState, useCallback, useEffect, useContext } from 'react';
import {
	Box,
	Button,
	Card,
	CardContent,
	IconButton,
	Typography,
	useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { withApplicationInsights } from '../../../components/telemetry';
import { MethodFormDialog, MethodFormValues } from '../../../components/coachingNavigator/createMethodPanel';
import ConfirmationPanel from '../../../components/confirmationPanel';
import { Method } from '../../../models';
import config from '../../../config';
import { MethodService } from '../../../services/coachingNavigator/methodService';
import { Context } from '../../../components/context';
import { CoachingPageHeader } from '../../../components/coachingNavigator/coachingPageHeader';

const MethodPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const theme = useTheme();
	const appContext = useContext(Context);
	const [methods, setMethods] = useState<Method[]>([]);
	const [loading, setLoading] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<Method | null>(null);
	const [editing, setEditing] = useState<Method | null>(null);

	const methodService = useMemo(() => new MethodService(config.api.baseUrl, '/method'), []);

	useEffect(() => {
		let isActive = true;
		setLoading(true);
		const creatorEmail = (appContext.state.invoker?.email || '').trim();
		const load = async () => {
			try {
				if (!creatorEmail) {
					if (isActive) setMethods([]);
					return;
				}
				const data = await methodService.getByCreator(creatorEmail);
				if (!isActive) return;
				const list = Array.isArray(data) ? data : [];
				setMethods(list);
			} catch (error) {
				console.error('Failed to load methods', error);
			} finally {
				if (isActive) setLoading(false);
			}
		};

		load();

		return () => {
			isActive = false;
		};
	}, [methodService, appContext.state.invoker]);

	const openCreate = useCallback(() => {
		setDialogOpen(true);
		setEditing(null);
	}, []);

	const openEdit = useCallback((method: Method) => {
		setEditing(method);
		setDialogOpen(true);
	}, []);

	const closeDialog = useCallback(() => {
		setDialogOpen(false);
		setEditing(null);
	}, []);

	const handleSave = useCallback(
		async (values: MethodFormValues) => {
			const creatorEmail = (appContext.state.invoker?.email || '').trim();
			const payload: Method = {
				id: editing?.id,
				name: values.name,
				description: values.summary,
				creator: editing?.creator || values.creator || (creatorEmail || undefined),
			};

			try {
				const saved = await methodService.save(payload);
				setMethods((prev) => {
					if (editing?.id) {
						return prev.map((m) => (m.id === saved.id ? saved : m));
					}
					return [...prev, saved];
				});
				setDialogOpen(false);
				setEditing(null);
			} catch (error) {
				console.error('Failed to save method', error);
			}
		},
		[editing?.creator, editing?.id, methodService, appContext.state.invoker]
	);

	const handleDelete = useCallback(async () => {
		if (!deleteTarget?.id) return;
		try {
			await methodService.delete(deleteTarget.id);
			setMethods((prev) => prev.filter((m) => m.id !== deleteTarget.id));
		} catch (error) {
			console.error('Failed to delete method', error);
		}
		setDeleteTarget(null);
	}, [deleteTarget, methodService]);

	const methodsEmpty = !loading && methods.length === 0;

	return (
        <Box sx={{ pt: 2 }}>
            <CoachingPageHeader
				onBack={() => navigate('/management')}
				title={t('coachingNavigator.methods.title')}
				subtitle={t('coachingNavigator.methods.subtitle')}
				rightContent={
					<Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none' }}>
						{t('coachingNavigator.methods.add')}
					</Button>
				}
				sx={{ mb: 1 }}
			/>
            <Box sx={{ mt: 1 }}>
				{loading ? (
					<Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
						<Typography variant="body2">{t('coachingNavigator.methods.loading')}</Typography>
					</Box>
				) : methodsEmpty ? (
					<Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
						<Typography variant="body1">{t('coachingNavigator.methods.emptyTitle')}</Typography>
						<Typography variant="body2" sx={{ mt: 0.5 }}>
							{t('coachingNavigator.methods.emptySubtitle')}
						</Typography>
					</Box>
				) : (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
						{methods.map((method) => (
							<Card
								key={method.id}
								variant="outlined"
								sx={{
									borderColor: 'divider',
									backgroundColor: 'white',
									borderRadius: 3,
									boxShadow: 0,
									transition: 'all 0.25s ease-out',
									'&:hover': {
										transform: 'translateY(-3px)',
										boxShadow: 4,
										borderColor: alpha(theme.palette.primary.main, 0.4),
										'.method-actions': { opacity: 1 },
									},
								}}
							>
								<CardContent
									sx={{
										p: 2,
										'&:last-child': { pb: 2 },
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'flex-start',
										gap: 2,
									}}
								>
									<Box sx={{ flex: 1 }}>
										<Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 600,
                                                color: "text.primary"
                                            }}>
											{method.name}
										</Typography>
										<Typography
                                            variant="body2"
                                            sx={{
                                                color: "text.secondary",
                                                mt: 0.5
                                            }}>
											{method.description || t('coachingNavigator.methods.noDescription')}
										</Typography>
									</Box>
									<Box className="method-actions" sx={{ display: 'flex', gap: 0.5, opacity: 0, transition: 'opacity 0.25s ease-out' }}>
										<IconButton size="small" onClick={() => openEdit(method)}>
											<EditOutlinedIcon fontSize="small" />
										</IconButton>
										<IconButton size="small" color="error" onClick={() => setDeleteTarget(method)}>
											<DeleteOutlineOutlinedIcon fontSize="small" />
										</IconButton>
									</Box>
								</CardContent>
							</Card>
						))}
					</Box>
				)}
			</Box>
            <MethodFormDialog
				open={dialogOpen}
				onClose={closeDialog}
				onSubmit={handleSave}
				title={editing ? t('coachingNavigator.methods.dialog.editTitle') : t('coachingNavigator.methods.dialog.createTitle')}
				submitLabel={editing ? t('coachingNavigator.methods.dialog.save') : t('coachingNavigator.methods.dialog.create')}
				initialValues={editing ? { name: editing.name, summary: editing.description || '', creator: editing.creator } : undefined}
				defaultCreatorEmail={appContext.state.invoker?.email}
			/>
            <ConfirmationPanel
				showModal={!!deleteTarget}
				hideModal={() => setDeleteTarget(null)}
				confirmModal={handleDelete}
				action="delete"
				title={t('coachingNavigator.methods.delete.title')}
				content={t('coachingNavigator.methods.delete.body')}
			/>
        </Box>
    );
};

export default withApplicationInsights(MethodPage, 'MethodPage');
